import { useRef } from "react";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { SplitText as GSAPSplitText } from "gsap/SplitText";
import { useGSAP } from "@gsap/react";

gsap.registerPlugin(useGSAP, ScrollTrigger, GSAPSplitText);

// Module-level defaults keep a stable identity, so parent re-renders don't restart the animation
const DEFAULT_FROM = { opacity: 0, y: 40 };
const DEFAULT_TO = { opacity: 1, y: 0 };

const SplitText = ({
  text,
  className = "",
  delay = 100,
  duration = 0.6,
  ease = "power3.out",
  splitType = "chars",
  from = DEFAULT_FROM,
  to = DEFAULT_TO,
  threshold = 0.1,
  rootMargin = "-100px",
  textAlign = "center",
  onLetterAnimationComplete,
}) => {
  const ref = useRef(null);
  const onCompleteRef = useRef(onLetterAnimationComplete);
  onCompleteRef.current = onLetterAnimationComplete;

  // useGSAP runs before paint (no flash of unanimated text) and its context
  // reverts only this component's split, tween and ScrollTrigger on cleanup
  useGSAP(
    () => {
      const el = ref.current;
      if (!el) return;

      const absoluteLines = splitType === "lines";
      if (absoluteLines) el.style.position = "relative";

      const splitter = new GSAPSplitText(el, {
        type: splitType,
        absolute: absoluteLines,
        linesClass: "split-line",
      });

      let targets;
      switch (splitType) {
        case "lines":
          targets = splitter.lines;
          break;
        case "words":
          targets = splitter.words;
          break;
        case "words, chars":
          targets = [...splitter.words, ...splitter.chars];
          break;
        default:
          targets = splitter.chars;
      }

      const startPct = (1 - threshold) * 100;
      const m = /^(-?\d+)px$/.exec(rootMargin);
      const raw = m ? parseInt(m[1], 10) : 0;
      const sign = raw < 0 ? `-=${Math.abs(raw)}px` : `+=${raw}px`;
      const start = `top ${startPct}%${sign}`;

      gsap.fromTo(
        targets,
        { ...from, willChange: "transform, opacity" },
        {
          ...to,
          duration,
          ease,
          stagger: delay / 1000,
          force3D: true,
          scrollTrigger: {
            trigger: el,
            start,
            once: true,
          },
          onComplete: () => {
            gsap.set(targets, { clearProps: "willChange" });
            onCompleteRef.current?.();
          },
        },
      );

      // The text renders hidden; reveal it only now that the letters are split
      // and set to their starting state, so it is never seen unanimated
      gsap.set(el, { visibility: "visible" });

      return () => splitter.revert();
    },
    {
      scope: ref,
      dependencies: [
        text,
        delay,
        duration,
        ease,
        splitType,
        JSON.stringify(from),
        JSON.stringify(to),
        threshold,
        rootMargin,
      ],
      revertOnUpdate: true,
    },
  );

  return (
    <p
      ref={ref}
      className={`split-parent overflow-hidden inline-block whitespace-normal ${className}`}
      style={{
        textAlign,
        wordWrap: "break-word",
        visibility: "hidden",
      }}
    >
      {text}
    </p>
  );
};

export default SplitText;
